import type { ComponentProps, ReactElement } from 'react';
import { useState } from 'react';

import type {
    ExtendedMaterialOnTheFly,
    MaterialOnTheFly,
} from '@buying/library';

import { KeyboardCombination } from '@bestseller-bit/frontend-community.components.keyboard-key';
import { EditIcon } from '@bestseller-bit/frontend-community.theme.buying-theme';
import {
    getMaterialOnTheFlyDescription,
    getMaterialOnTheFlyTitle,
} from '@buying/library';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { isWindows } from 'react-device-detect';

import { MaterialInformation } from './MaterialInformation';
import { MenuItemThumbnail } from './MenuItemThumbnail';

interface MenuItemMaterialOnTheFlyProps
    extends ComponentProps<typeof MenuItem> {
    mof: ExtendedMaterialOnTheFly | MaterialOnTheFly;
    isFocused?: boolean;
    disableEdit?: boolean;
    onEditClick?: IconButtonProps['onClick'];
    /** The search query to highlight in the material text */
    highlight?: string;
}

const MenuItemMaterialOnTheFly = ({
    mof,
    isFocused = false,
    disableEdit = false,
    onEditClick: handleEditClick,
    highlight,
    ...menuItemProps
}: MenuItemMaterialOnTheFlyProps): ReactElement => {
    const [isMenuItemHovered, setIsMenuItemHovered] = useState<boolean>(false);
    const [isIconButtonHovered, setIsIconButtonHovered] =
        useState<boolean>(false);

    const nameText = getMaterialOnTheFlyTitle(mof);
    const detailText = getMaterialOnTheFlyDescription(mof);

    return (
        <MenuItem
            {...menuItemProps}
            value={mof.materialOnTheFlyPublicId}
            style={{
                padding: 0,
                ...menuItemProps.style,
            }}
            sx={{
                p: 0,
                m: 0,
                flexGrow: 1,
                height: 1,
                // eslint-disable-next-line @typescript-eslint/no-misused-spread
                ...menuItemProps.sx,
            }}
            onMouseEnter={() => {
                setIsMenuItemHovered(true);
            }}
            onMouseLeave={() => {
                setIsMenuItemHovered(false);
            }}
        >
            <Stack
                direction="row"
                sx={{ width: 1, height: 1 }}
            >
                <MenuItemThumbnail
                    alt={`attachment-for-${mof.materialOnTheFlyPublicId}`}
                    src={undefined}
                />
                <MaterialInformation
                    headerText={mof.materialSubcategory.materialSubcategoryName}
                    nameText={nameText}
                    detailText={detailText}
                    highlight={highlight}
                />
                {!disableEdit && (
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            justifyContent: 'flex-end',
                            flexGrow: 1,
                        }}
                    >
                        {isIconButtonHovered && (
                            <KeyboardCombination
                                keys={[isWindows ? 'alt' : '⌥', 'e']}
                            />
                        )}
                        {(isMenuItemHovered || isFocused) && (
                            <IconButton
                                variant="text"
                                sx={{
                                    marginLeft: 'auto',
                                }}
                                onClick={handleEditClick}
                                onMouseEnter={() => {
                                    setIsIconButtonHovered(true);
                                }}
                                onMouseLeave={() => {
                                    setIsIconButtonHovered(false);
                                }}
                            >
                                <EditIcon
                                    style={{ cursor: 'pointer' }}
                                    size={16}
                                />
                            </IconButton>
                        )}
                    </Stack>
                )}
            </Stack>
        </MenuItem>
    );
};

export default MenuItemMaterialOnTheFly;
